import { Component, Event, EventEmitter, h, Prop, State, Element, Fragment, Method, Watch } from '@stencil/core';
import { Unsubscribe } from 'redux';
import { RootState } from '../../../redux/reducers';
import { store } from '../../../redux/store';
import { Translations } from '../../../redux/types';
import rentwareIcon from '../../../types/icons';
import { SelectOption, SelectStyle, SelectType } from '../../../types/types';
import { trans } from '../../../utils/trans';

@Component({
  tag: 'rentware-select',
  styleUrl: 'style.sass',
  shadow: false,
})
export class RentwareSelect {
  @Prop() selectStyle: SelectStyle = SelectStyle.Default;
  @Prop() selectType: SelectType = SelectType.Single;
  @Prop() selectTextKey: string = '';
  @Prop() expandedStyle?: SelectStyle = this.selectStyle;
  @Prop() wasTouched: boolean = false;
  @Prop() options: SelectOption[] = [];
  @Prop() price: string = '';
  @Prop() pricePerPeriod: string = '';
  @Prop() label: string = '';
  @Prop() placeholder?: string = '';
  @Prop() name?: string;
  @Prop() required?: boolean;
  @Prop() onBlur?: any;
  @Prop() onFocus?: any;
  @Prop() inputRef?: any;
  @Prop() inputUnRef?: any;
  @Prop() error: boolean = false;
  @Prop() disabled: boolean = false; // works only in search style + variations for now
  @Prop() scrollToClick: boolean = false;
  @Prop() customIconName?: string;
  @Prop() customIconStyle?: string;
  @Prop() doNotCloseIfClickedOnElementWithId?: string;
  @Prop() noEmptyValue: boolean = false;
  @Prop() searchable: boolean = false;

  @State() expanded: boolean = false;
  @State() closingMenu: boolean = false;
  @State() openUp: boolean = false;
  @State() alreadySelected: boolean = false;
  @State() valuesToUpdate: string[] = []; //multi select only
  @State() filterInput: string = '';

  @Prop({ mutable: true }) translations: Translations;
  @Prop({ mutable: true }) locale: string;
  @Prop({ mutable: true }) value: string | string[];

  @Element() el: HTMLInputElement;
  filterInputEl: HTMLInputElement;

  inputInternalRef?: any;
  unsubscribe!: Unsubscribe;
  expandedPickerElement: HTMLElement;

  @Event() valueChange: EventEmitter;

  @Method()
  openSelectFromOutside() {
    this.expanded = true;
  }

  @Watch('expanded')
  expandedHasChanged(newValue: boolean, oldValue: boolean) {
    if (newValue !== oldValue) {
      if (newValue) {
        this.filterInputEl?.focus();
        this.filterInput = '';
      } else {
        this.filterInputEl?.blur();
      }
    }
  }

  // when current option becomes disabled change it to first not disabled in possible
  @Watch('options')
  optionsHasChanged(newOptions: SelectOption[]) {
    //non empty only for signle select
    const newSelected = newOptions.find(option => option.value === this.value);

    const firstNotDisabled: SelectOption = newOptions.find((item: SelectOption) => {
      return !item.disabled;
    });

    if (
      newSelected?.disabled &&
      !!firstNotDisabled && //
      firstNotDisabled.value !== this.value
    ) {
      this.inputInternalRef.value = firstNotDisabled.value;
      this.value = firstNotDisabled.value;
      this.valueChange.emit(firstNotDisabled);
    }
  }

  disconnectedCallback() {
    if (this.inputUnRef) {
      this.inputUnRef(this.inputInternalRef, this.name);
    }
  }

  componentWillLoad() {
    this.unsubscribe = store.mapStateToProps(this, (state: RootState) => {
      return {
        translations: state.system.translations,
        locale: state.app.locale,
      };
    });

    if (this.selectType === SelectType.Multi && typeof this.value !== 'string') {
      this.valuesToUpdate = this.value;
    }
  }

  componentDidLoad() {
    const firstNotDisabled: SelectOption = this.options.find((item: SelectOption) => {
      return !item.disabled;
    });

    const currentOption: SelectOption = this.options.find((item: SelectOption) => {
      return item.value === this.value;
    });

    if (
      currentOption?.disabled &&
      !!firstNotDisabled && //
      firstNotDisabled.value !== this.value
    ) {
      this.inputInternalRef.value = firstNotDisabled.value;
      this.value = firstNotDisabled.value;
      this.valueChange.emit(firstNotDisabled);
    }

    if (this.noEmptyValue && !this.value) {
      const firstNotDisabledValue: string = this.options.find((item: SelectOption) => {
        return !item.disabled;
      })?.value;

      this.inputInternalRef.value = firstNotDisabledValue;
      this.value = firstNotDisabledValue;
      this.valueChange.emit(firstNotDisabledValue);
      this.alreadySelected = true;
    }
  }

  handleExpandedDidLoad = () => {
    this.expandedPickerElement.scrollIntoView({ block: 'end', behavior: 'smooth' });
  };

  handleOptionsChange = newValues => {
    this.valuesToUpdate = newValues;
  };

  handleOptionChange = (e: CustomEvent) => {
    e.stopPropagation();

    const eventItem: SelectOption = e.detail;
    if (!eventItem.disabled || this.allOptionsAreDisabled) {
      this.inputInternalRef.value = eventItem.value;
      this.value = eventItem.value;
      this.valueChange.emit(eventItem);
      this.alreadySelected = true;
      this.closeMenu();
    }
  };

  closeMenu = () => {
    this.closingMenu = true;

    if (this.selectType === SelectType.Multi && typeof this.value !== 'string') {
      this.inputInternalRef.value = this.valuesToUpdate;
      this.valueChange.emit({ value: this.valuesToUpdate });
      this.alreadySelected = true;
    }

    setTimeout(() => {
      this.expanded = false;
      this.closingMenu = false;
    }, 250);
  };

  handleClick = () => {
    if (!this.expanded && this.scrollToClick) {
      setTimeout(() => this.el.scrollIntoView({ behavior: 'smooth' }), 250);
    }

    if (!this.disabled && !this.singleOption) {
      this.expanded = true;
      if (this.onFocus) {
        this.onFocus();
      }
    }
  };

  onClickOutside = event => {
    const elementClicked = event.detail.target;

    if (this.doNotCloseIfClickedOnElementWithId && elementClicked.closest(`#${this.doNotCloseIfClickedOnElementWithId}`)?.id === this.doNotCloseIfClickedOnElementWithId) {
      return;
    }

    this.closeMenu();
    if (this.onBlur) {
      this.onBlur();
    }

    if (this.selectType === SelectType.Multi && typeof this.value !== 'string') {
      this.inputInternalRef.value = this.valuesToUpdate;
      this.valueChange.emit({ value: this.valuesToUpdate });
      this.alreadySelected = true;
    }
  };

  handleChangeInput = (e: InputEvent) => {
    e.preventDefault();
    if (this.filterInput === '') {
    }
    this.filterInput = (e.target as HTMLInputElement).value;
  };

  get allOptionsAreSelected() {
    return JSON.stringify(this.valuesToUpdate.sort()) === JSON.stringify(this.options?.map(option => option.value).sort());
  }

  get selectText(): string {
    const key = this.selectTextKey  ? this.selectTextKey : 'config.select'

    return trans(key, this.locale, this.translations);
  }

  get allSelectedText(): string {
    return trans(`config.allSelected`, this.locale, this.translations);
  }

  get noneSelectedText(): string {
    return trans(`config.noneSelected`, this.locale, this.translations);
  }

  get displayValueWithFilter(): string {
    if (this.expanded) {
      return this.filterInput;
    }

    return this.displayValue;
  }

  get displayValue(): string {
    if ((!!this.placeholder && !this.alreadySelected) || !this.value) {
      return !!this.placeholder ? this.placeholder : this.selectText;
    }

    if (typeof this.value === 'string') {
      return this.options?.find(item => item.value === this.value)?.label;
    } else {
      if (this.allOptionsAreSelected) {
        return this.allSelectedText;
      } else {
        return (
          this.valuesToUpdate
            .map(value => {
              return this.options?.find(item => item.value === value)?.label;
            })
            .join(', ') || this.noneSelectedText
        );
      }
    }
  }

  get allOptionsAreDisabled(): boolean {
    return this.options.every((item: SelectOption) => {
      return item.disabled;
    });
  }

  get wrapperClass() {
    let wrapperClass = this.selectStyle + 'Wrapper ';

    if (!this.disabled && !this.singleOption) {
      wrapperClass += this.selectStyle + 'WrapperActive ';
    }

    if (this.disabled) {
      wrapperClass += this.selectStyle + 'WrapperDisabled ';
    }

    if (this.error) {
      wrapperClass += 'Error ';
    }

    if (this.wasTouched) {
      wrapperClass += 'Touched ';
    }

    return wrapperClass;
  }

  get menuWrapperClass() {
    let menuWrapperClass = 'MenuWrapper ' + this.selectStyle + 'MenuWrapper ';

    if (this.openUp) {
      menuWrapperClass += 'openUp ';
    } else {
      menuWrapperClass += 'openDown ';
    }

    if (this.closingMenu) menuWrapperClass += 'closing ';

    return menuWrapperClass;
  }

  get inputClass() {
    return 'Input ' + this.selectStyle + 'Input ';
  }

  get contentClass() {
    let contentClass = 'Content';

    if (this.singleOption) {
      contentClass += ' ContentNoChoice';
    }

    if (this.selectStyle === SelectStyle.Default) {
      if (this.disabled) {
        contentClass += ' ContentDisabled';
      } else if (!this.searchable) {
        contentClass += ' ContentDefaultActive';
      }
    }

    if (this.selectStyle === SelectStyle.Config || this.selectStyle === SelectStyle.NewConfig) {
      if (this.disabled) {
        contentClass += ' ContentDisabled';
      } else if (!this.searchable) {
        contentClass += ' ContentDefaultActive';
      }
    }

    if (this.selectStyle === SelectStyle.Simple) {
      contentClass += ' ContentSimple';
    }

    if (this.selectStyle === SelectStyle.Search) {
      contentClass += ' ContentSearch';
      if (this.disabled) {
        contentClass += ' ContentSearchDisabled';
      }
    }

    if (this.selectStyle === SelectStyle.Variations) {
      contentClass += ' ContentVariations';
      if (this.disabled) {
        contentClass += ' ContentSearchDisabled';
      }
    }

    return contentClass;
  }

  get selectMenuClass() {
    let selectMenuClass = 'SelectMenu';
    if (this.selectStyle === SelectStyle.Config || this.selectStyle === SelectStyle.NewConfig) {
      selectMenuClass += ' FixedStyle';
    }

    return selectMenuClass;
  }

  get singleOption(): boolean {
    if (!this.value && this.options?.length > 0) {
      return false
    }

    return !(this.options?.length > 1); // empty also count as single option. Generally it should not happen, but in this case is better not to expand picker
  }

  get calculatedExpandedStyle() {
    if (this.expandedStyle === SelectStyle.NewConfig) {
      return SelectStyle.Config;
    }

    return this.expandedStyle;
  }

  get filteredOptions(): SelectOption[] {
    return this.options.filter((option: SelectOption) => {
      return (
        option.value.toLowerCase().includes(this.filterInput.toLowerCase()) || //
        option.label.toLowerCase().includes(this.filterInput.toLowerCase())
      );
    });
  }

  render() {
    return (
      <div class={this.wrapperClass} tabIndex={(this.disabled && -1) || 0}>
        {this.selectStyle === SelectStyle.Default && <label class={{ SelectLabel: true, LabelError: this.error, Touched: this.wasTouched }}>{this.label}</label>}

        <input
          class={this.inputClass}
          type="text"
          name={this.name}
          required={this.required}
          ref={(el: HTMLInputElement) => {
            this.inputInternalRef = el;
            if (this.inputRef) {
              this.inputRef(el);
            }
          }}
          value={this.value}
          onFocus={this.onFocus}
          aria-hidden="true"
          tabIndex={-1}
        />

        <div class={this.contentClass} onClick={this.handleClick}>
          {this.selectStyle === SelectStyle.Default && (
            <Fragment>
              {this.value && (
                <svg width="17" height="17" viewBox="0 0 20 21" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="0.5" y="0.879395" width="19" height="19" rx="9.5" />
                  <path d="M6 11.3794L8 13.3794L14 7.37939" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              )}

              {!this.searchable && <div class="ValueDivDefault" innerHTML={this.displayValue} />}

              {this.searchable && (
                <input
                  ref={(el: HTMLInputElement) => {
                    this.filterInputEl = el;
                  }}
                  class="ValueInputDefault"
                  value={this.displayValueWithFilter}
                  placeholder={this.displayValue}
                  onInput={this.handleChangeInput}
                />
              )}

              {!!this.price && <span class="EllipsisValue SpanNoShrink">{this.price}</span>}

              {!this.singleOption && (
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              )}
            </Fragment>
          )}

          {(this.selectStyle === SelectStyle.Config || this.selectStyle === SelectStyle.NewConfig) && (
            <div class="ValueContainerConfig">
              {rentwareIcon(this.customIconName, this.customIconStyle)}

              {!this.searchable && (
                <div class="ConfigTextContainer">
                  <span class="ConfigLabel">{this.label}</span>

                  <span class="EllipsisValue">{this.displayValue}</span>
                </div>
              )}

              {this.searchable && (
                <div class="ConfigTextContainer">
                  <span class="ConfigLabel">{this.label}</span>

                  <input
                    ref={(el: HTMLInputElement) => {
                      this.filterInputEl = el;
                    }}
                    class="ValueInputConfig"
                    value={this.displayValueWithFilter}
                    placeholder={this.displayValue}
                    onInput={this.handleChangeInput}
                  />
                </div>
              )}

              {!this.singleOption && (
                <svg width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1L5 5L9 1" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              )}
            </div>
          )}

          {this.selectStyle === SelectStyle.Simple && (
            <div class={`${this.singleOption ? 'ValueContainerSimple--no-svg--no-border' : 'ValueContainerSimple'}`}>
              <span class="EllipsisValue" innerHTML={this.displayValue} />

              {!this.singleOption && (
                <Fragment>
                <div class={"VerticalDividerWrapper"}>
                <div class={"VerticalDivider"} />
                </div>
                <svg class="SmallMargin" width="10" height="6" viewBox="0 0 10 6" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1.4082 1L5.4082 5L9.4082 1" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
                </Fragment>
              )}
            </div>
          )}

          {this.selectStyle === SelectStyle.Search && (
            <div class="ValueContainerSearch">
              <div class="ValueContainerSearchInner">
                <svg width="21" height="28" viewBox="0 0 21 28" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10.3699 27.2946C10.3318 27.2631 10.2903 27.2286 10.2458 27.1913C9.96773 26.9585 9.56879 26.6141 9.08929 26.1711C8.12964 25.2845 6.85059 24.006 5.57267 22.4389C3.00406 19.2891 0.5 15.0463 0.5 10.5157C0.5 5.01528 4.92297 0.562744 10.3704 0.562744C15.8178 0.562744 20.2407 5.01528 20.2407 10.5157C20.2407 15.0345 17.7368 19.2773 15.1679 22.4304C13.8899 23.999 12.6108 25.2797 11.6511 26.1681C11.1715 26.6121 10.7726 26.9573 10.4945 27.1907C10.4498 27.2283 10.4082 27.2629 10.3699 27.2946ZM10.3704 6.53139C8.18349 6.53139 6.41892 8.31978 6.41892 10.5157C6.41892 12.7116 8.18349 14.5 10.3704 14.5C12.5572 14.5 14.3218 12.7116 14.3218 10.5157C14.3218 8.31978 12.5572 6.53139 10.3704 6.53139Z" />
                </svg>

                <div class="ValueContainerSearchSpan EllipsisValue" innerHTML={this.displayValue} />
              </div>

              {!this.singleOption && (
                <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.40332L5 5.40332L9 1.40332" stroke="#46435B" stroke-linecap="round" />
                </svg>
              )}
            </div>
          )}

          {this.selectStyle === SelectStyle.Variations && (
            <div class="ValueContainerVariations">
              <div class="ValueContainerVariationsInner">
                <span class="EllipsisValue">{this.label}</span>

                {!!this.price && <span class="EllipsisValue SpanNoShrink">{this.price}</span>}

                <div class="ValueContainerSearchSpan EllipsisValue BoldSpan" innerHTML={this.displayValue} />
              </div>

              {!this.singleOption && (
                <svg width="10" height="7" viewBox="0 0 10 7" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 1.40332L5 5.40332L9 1.40332" stroke-linecap="round" />
                </svg>
              )}
            </div>
          )}
        </div>

        {this.expanded && (
          <div
            class={{ SelectExpandedContainer: true, SelectExpandedMargin: this.selectStyle === SelectStyle.NewConfig }}
            ref={el => (this.expandedPickerElement = el as HTMLElement)}
          >
            <rentware-wrapper class={this.menuWrapperClass} onClickOutside={this.onClickOutside}>
              <div class={this.selectMenuClass}>
                {this.selectType === SelectType.Single && !Array.isArray(this.value) && (
                  <rentware-selection-list
                    selectStyle={this.calculatedExpandedStyle}
                    options={this.filteredOptions}
                    value={this.value}
                    onValueChange={this.handleOptionChange}
                    onExpandedDidLoad={this.handleExpandedDidLoad}
                    customIconName={this.customIconName}
                  />
                )}

                {this.selectType === SelectType.Multi && (
                  <rentware-selection-list-multi
                    selectStyle={this.calculatedExpandedStyle}
                    options={this.filteredOptions}
                    values={this.valuesToUpdate}
                    onExpandedDidLoad={this.handleExpandedDidLoad}
                    onUpdateValues={this.handleOptionsChange}
                    translations={this.translations}
                    locale={this.locale}
                    onClose={this.closeMenu}
                  />
                )}
              </div>
            </rentware-wrapper>

            <rentware-spacer size="M" />
          </div>
        )}
      </div>
    );
  }
}
